import type { JSX, ReactNode } from "react";

/**
 * The editor's icons, drawn here so that no icon package comes with it. One 16 px grid and one
 * stroke, in the text color; `1em` square, so `font-size` on the button sizes them.
 */
export type IconName =
  | "bold"
  | "italic"
  | "strikethrough"
  | "link"
  | "listNumbered"
  | "listBulleted"
  | "listCheck"
  | "code"
  | "codeBlock"
  | "send";

const SHAPES: Record<IconName, ReactNode> = {
  bold: (
    <path
      strokeWidth="1.9"
      d="M4.75 2.75h3.9a2.4 2.4 0 0 1 0 4.8h-3.9zM4.75 7.55h4.5a2.85 2.85 0 0 1 0 5.7h-4.5z"
    />
  ),
  italic: <path d="M6.75 2.75h4.5M4.75 13.25h4.5M9.4 2.75 6.6 13.25" />,
  strikethrough: (
    <path d="M2.25 8h11.5M11.4 4.9c-.35-1.3-1.6-2.15-3.35-2.15-2 0-3.3 1-3.3 2.45 0 .75.35 1.35 1 1.8M4.6 11.1c.4 1.3 1.65 2.15 3.45 2.15 2.05 0 3.35-1 3.35-2.5 0-.4-.1-.75-.25-1.05" />
  ),
  link: (
    <path d="M6.9 9.1a2.9 2.9 0 0 0 4.1 0l2.1-2.1A2.9 2.9 0 0 0 9 2.9l-.8.8M9.1 6.9a2.9 2.9 0 0 0-4.1 0L2.9 9A2.9 2.9 0 0 0 7 13.1l.8-.8" />
  ),
  listNumbered: (
    <>
      <path d="M7 4h6.75M7 8h6.75M7 12h6.75" />
      <path
        strokeWidth="1.15"
        d="m2.6 3.3 1.15-.8v3.75M2.4 10.35c.1-.6.55-.95 1.15-.95.65 0 1.1.4 1.1.95 0 1-2.25 1.6-2.25 2.9h2.4"
      />
    </>
  ),
  listBulleted: (
    <>
      <path d="M7 4h6.75M7 8h6.75M7 12h6.75" />
      <path strokeWidth="2" d="M3.25 4h.01M3.25 8h.01M3.25 12h.01" />
    </>
  ),
  listCheck: (
    <>
      <path d="M8 4.5h5.75M8 11.5h5.75" />
      <path d="m2.25 4.6 1.3 1.3 2.4-2.8M2.5 9.75h3.25V13H2.5z" />
    </>
  ),
  code: <path d="M5 4.75 1.75 8 5 11.25M11 4.75 14.25 8 11 11.25M9.3 3.25l-2.6 9.5" />,
  codeBlock: (
    <>
      <path d="M3.75 2.25h8.5a1.5 1.5 0 0 1 1.5 1.5v8.5a1.5 1.5 0 0 1-1.5 1.5h-8.5a1.5 1.5 0 0 1-1.5-1.5v-8.5a1.5 1.5 0 0 1 1.5-1.5z" />
      <path d="M6.6 6.1 4.85 8l1.75 1.9M9.4 6.1 11.15 8 9.4 9.9" />
    </>
  ),
  send: <path d="M14.25 1.75 7.4 8.6M14.25 1.75 9.9 14.25 7.4 8.6 1.75 6.1z" />,
};

export function Icon({ name }: { name: IconName }): JSX.Element {
  return (
    <svg
      className="ze-icon"
      viewBox="0 0 16 16"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {SHAPES[name]}
    </svg>
  );
}
